<template>
  <div class="mt-5">
    <div ref="paymentElementElement"></div>
    <button
      class="menu-button-primary mt-6 w-full"
      type="button"
      :disabled="submitting"
      @click="submit"
    >
      {{ submitting ? 'Processing...' : 'Complete Payment' }}
    </button>
  </div>
</template>

<script setup lang="ts">
import { loadStripe } from '@stripe/stripe-js/pure';
import { onMounted, ref, useTemplateRef } from 'vue';
import { useToast } from 'vue-toastification';
import { Stripe, StripeElements } from '@stripe/stripe-js';

const props = defineProps<{
  publishableKey: string;
  clientSecret: string;
  stripeAccount: string;
  returnUrl: string;
}>();

const toast = useToast();

const submitting = ref(false);

let stripe: Stripe | null = null;
let elements: StripeElements | null = null;

const paymentElementElement = useTemplateRef('paymentElementElement');

onMounted(async () => {
  if (!paymentElementElement.value) {
    throw new Error('Payment element required.');
  }

  stripe = await loadStripe(props.publishableKey, {
    stripeAccount: props.stripeAccount,
  });

  if (!stripe) {
    throw new Error('Failed to load Stripe.');
  }

  const appearance = {/* appearance */};
  const options = {
    paymentMethodOrder: ['apple_pay', 'google_pay', 'card'],
  };

  elements = stripe.elements({
    clientSecret: props.clientSecret,
    appearance,
  });
  const paymentElement = elements.create('payment', options);
  paymentElement.mount(paymentElementElement.value);
});

const submit = async () => {
  submitting.value = true;

  if (!stripe) {
    throw new Error('Failed to load Stripe.');
  }

  if (!elements) {
    throw new Error('Failed to load Stripe Elements.');
  }

  const { error } = await stripe.confirmPayment({
    elements: elements,
    confirmParams: {
      // Make sure to change this to your payment completion page
      return_url: props.returnUrl,
    },
  });

  // This point will only be reached if there is an immediate error when
  // confirming the payment. Otherwise, your customer will be redirected to
  // your `return_url`. For some payment methods like iDEAL, your customer will
  // be redirected to an intermediate site first to authorize the payment, then
  // redirected to the `return_url`.
  if (error.type === 'card_error' || error.type === 'validation_error') {
    toast.error(error.message, {
      timeout: 3000,
    });
  } else {
    toast.error('An unexpected error occurred.', {
      timeout: 3000,
    });
  }

  submitting.value = false;
};
</script>
